/**
 * Metadata-Driven Form Component (Form Factory)
 * 
 * WHY: This is the cornerstone of the "Salesforce pattern" mandated in
 * SPECTREIQ_MASTER.md. Forms are generated dynamically from MetadataIQ
 * schema definitions, never hardcoded.
 * 
 * This component:
 * 1. Fetches schema definition from MetadataIQ API
 * 2. Generates Zod validation schema dynamically
 * 3. Renders form fields using DynamicField component
 * 4. Handles conditional field visibility and required status
 * 5. Wrapped in ErrorBoundary for fault tolerance
 * 
 * @see .docs/architecture/software_iq_dashboard.md - Section 2.2
 * @see .docs/architecture/software_iq_architecture_review.md - Section 3.1
 */

import { useMemo, useCallback } from 'react'
import { useForm, FieldValues } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { api, Schema } from '@/api'
import { generateConditionalZodSchema, isFieldVisible, isFieldRequired } from '@/lib/schema-to-zod'
import { DynamicField } from './DynamicField'
import { FormErrorBoundary } from './FormErrorBoundary'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Loader2 } from 'lucide-react'

/**
 * Props for MetadataForm component.
 * 
 * @template T - Expected form data shape (for type-safe onSubmit)
 */
interface MetadataFormProps {
  /** Schema ID to fetch from MetadataIQ (e.g., 'softwareiq-finding') */
  schemaId: string
  /** Initial form values for edit mode */
  initialData?: Partial<FieldValues>
  /** Callback when form is submitted with valid data */
  onSubmit: (data: FieldValues) => void | Promise<void>
  /** Callback when form submission fails */
  onError?: (error: Error) => void
  /** Submit button text */
  submitLabel?: string
  /** Whether submission is in progress */
  isSubmitting?: boolean
  /** Additional CSS classes for the form container */
  className?: string
  /** Hide the card wrapper and render fields directly */
  inline?: boolean
}

/**
 * Skeleton loader for form loading state.
 */
function FormSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-4 w-24 bg-muted rounded" />
          <div className="h-10 w-full bg-muted rounded" />
        </div>
      ))}
      <div className="h-10 w-28 bg-muted rounded" />
    </div>
  )
}

/**
 * Internal form component that renders once schema is loaded.
 * WHY: Uses FieldValues for dynamic forms where exact type isn't known at compile time.
 */
function MetadataFormInner({
  schema,
  initialData,
  onSubmit,
  onError,
  submitLabel = 'Submit',
  isSubmitting = false,
}: {
  schema: Schema
  initialData?: Partial<FieldValues>
  onSubmit: (data: FieldValues) => void | Promise<void>
  onError?: (error: Error) => void
  submitLabel?: string
  isSubmitting?: boolean
}) {
  // Generate Zod schema with conditional validation
  const zodSchema = useMemo(() => generateConditionalZodSchema(schema), [schema])

  const form = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: initialData,
    mode: 'onBlur', // Validate on blur for better UX
  })

  // Watch all form values for conditional logic
  const formValues = form.watch()

  // Handle form submission
  const handleSubmit = useCallback(
    async (data: FieldValues) => {
      try {
        await onSubmit(data)
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error(String(error)))
      }
    },
    [onSubmit, onError]
  )

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
      <div className="space-y-4">
        {schema.fields.map((field) => {
          const isHidden = !isFieldVisible(field, formValues as Record<string, unknown>)
          const isConditionallyRequired = isFieldRequired(field, formValues as Record<string, unknown>)

          return (
            <DynamicField
              key={field.field_id}
              field={field}
              control={form.control}
              isHidden={isHidden}
              isConditionallyRequired={isConditionallyRequired}
            />
          )
        })}
      </div>

      <div className="flex items-center gap-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
        {form.formState.errors.root && (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        )}
      </div>
    </form>
  )
}

/**
 * Metadata-driven form component.
 * 
 * @example
 * <MetadataForm
 *   schemaId="softwareiq-finding"
 *   onSubmit={(data) => api.softwareiq.findings.create(missionId, data)}
 *   submitLabel="Create Finding"
 * />
 */
export function MetadataForm({
  schemaId,
  initialData,
  onSubmit,
  onError,
  submitLabel,
  isSubmitting,
  className,
  inline = false,
}: MetadataFormProps) {
  // Fetch schema from MetadataIQ
  const {
    data: schema,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['metadata', 'schemas', schemaId],
    queryFn: () => api.metadata.schemas.get(schemaId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  })

  // Handle loading state
  if (isLoading) {
    if (inline) {
      return <FormSkeleton />
    }
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <FormSkeleton />
        </CardContent>
      </Card>
    )
  }

  // Handle error state
  if (error || !schema) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load form schema'
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-destructive">{errorMessage}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Render form wrapped in error boundary
  const formContent = (
    <FormErrorBoundary schemaId={schemaId} onRetry={() => refetch()}>
      <MetadataFormInner
        schema={schema}
        initialData={initialData}
        onSubmit={onSubmit}
        onError={onError}
        submitLabel={submitLabel}
        isSubmitting={isSubmitting}
      />
    </FormErrorBoundary>
  )

  if (inline) {
    return <div className={className}>{formContent}</div>
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{schema.display_name || schema.name}</CardTitle>
        {schema.description && <CardDescription>{schema.description}</CardDescription>}
      </CardHeader>
      <CardContent>{formContent}</CardContent>
    </Card>
  )
}

/**
 * Hook to fetch and use a schema for custom form implementations.
 * 
 * WHY: Some forms may need custom layouts but still want schema-driven
 * validation. This hook provides the schema data without the UI.
 */
export function useSchema(schemaId: string) {
  return useQuery({
    queryKey: ['metadata', 'schemas', schemaId],
    queryFn: () => api.metadata.schemas.get(schemaId),
    staleTime: 5 * 60 * 1000,
  })
}
